import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useDialogs, EventPreviewPopper, useClientOptions, useUsers } from '@paintscout/ui-business'
import React from 'react'
import type { EventProps } from 'react-big-calendar'
import moment from 'moment-timezone'
import type { CalendarEvent } from 'paintscout'
import { useCancelEventFlow } from 'src/hooks'
import classnames from 'classnames'
import { formatName, getTimezone, translateEventType } from '@paintscout/util/builder'
import { getEventLabel } from '@paintscout/util'
import { EventDialog } from '../../../dialogs'
import { useCalendar } from '../CalendarContext'

const GOOGLE_CALENDAR_EVENT_TYPE = '_google-calendar_'

const useStyles = makeStyles<Theme, { miniEvent?: boolean }>((theme) => ({
  root: {
    height: '100%',
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.paintscout.palette.light,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: ({ miniEvent }) => (miniEvent ? 'center' : 'flex-start')
  },
  timeRange: {
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  unavailable: {
    display: 'flex',
    alignItems: 'center !important',
    justifyContent: 'center',
    position: 'relative', // Needed for pseudo-element
    overflow: 'hidden',
    backgroundImage: `
    repeating-linear-gradient(
      135deg,
      ${theme.paintscout.palette?.brand?.Neutral?.P98},
      ${theme.paintscout.palette?.brand?.Neutral?.P98} 10px,
      ${theme.paintscout.palette?.brand?.Neutral?.P94} 10px,
      ${theme.paintscout.palette?.brand?.Neutral?.P94} 12px
    )
  `,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backdropFilter: 'blur(1px)', // Adjust blur as needed
      zIndex: 1
    },
    '&:hover $unavailableText': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  unavailableText: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderRadius: theme.borderRadius.md,
    position: 'relative',
    zIndex: 2,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s'
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  }
}))

export default function DayEvent(props: EventProps<CalendarEvent>) {
  const { updateEvent, deleteEvent } = useCalendar()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { users } = useUsers()
  const { event } = props
  const { start, end, estimator, contact } = event
  const miniEvent = moment(end).diff(moment(start), 'minutes') <= 30
  const classes = useStyles({ miniEvent })
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const eventType =
    event.eventType === GOOGLE_CALENDAR_EVENT_TYPE
      ? event.title
      : translateEventType({ eventType: event.eventType, options })

  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  const handleEditEvent = () => {
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
        setAnchorEl(null)
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  const DayEventContent = () => {
    if (miniEvent) {
      return (
        <Typography variant="body2" weight={600}>
          {eventType}
          {contact && <> - {formatName(contact)}</>}
        </Typography>
      )
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="body2" weight={600}>
          {eventType}
          {contact && <> - {formatName(contact)}</>}
        </Typography>
        <Typography variant="body2" style={{ fontSize: 12 }}>
          {getEventLabel(moment(event.start).tz(timezone), moment(event.end).tz(timezone), false)}
        </Typography>
      </div>
    )
  }

  if (eventType === '_unavailable_') {
    return (
      <div className={classnames(classes.root, classes.unavailable, 'rbc-event-unavailable')}>
        <Typography variant="body2" className={classes.unavailableText} weight={500}>
          Unavailable
        </Typography>
      </div>
    )
  }

  return (
    <>
      <div
        className={classnames(
          classes.root,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? classes.integrationOverlay : null,
          event.eventType === GOOGLE_CALENDAR_EVENT_TYPE ? 'rbc-event-imported' : null
        )}
        onClick={handleClick}
        style={{ backgroundColor: estimator?.color }}
      >
        <DayEventContent />
      </div>
      <EventPreviewPopper
        event={event}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        users={users}
        onClose={() => setAnchorEl(null)}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
        minWidth={330}
      />
    </>
  )
}
